import { getOrganizationConsumptionDashboard } from '../../../services/licensing/ConsumptionDashboardService';
import type {
    IUserUsageOrgSummaryDto,
    IUserUsageUserConsumptionDto,
} from '../../../services/licensing/UserUsageService';
import {
    countUsersWithConsumption,
    getSingleUserConsumption,
    getUsersConsumption,
} from '../../../services/licensing/UserUsageService';
import type {
    PeriodOption,
    UserUsageDistribution,
    UserUsageRow,
    UserUsageStatus,
} from './types';

export interface UserUsageBundle {
    users: UserUsageRow[];
    needsAttention: UserUsageRow[];
    periodOptions: PeriodOption[];
    totalUsersWithConsumption: number;
    pageTotalCount: number;
}

const STATUS_MAP: Record<string, UserUsageStatus> = {
    MonthlyLimit: 'active',
    TopUp: 'onTopUp',
    PaidUnits: 'consumingPaidUnits',
    '0': 'active',
    '1': 'onTopUp',
    '2': 'consumingPaidUnits',
};

const LICENSE_NOT_ACQUIRED_STATUSES = new Set([ 'LicenseNotAcquired', '3' ]);

const isLicenseNotAcquired = (dto: IUserUsageUserConsumptionDto): boolean =>
    LICENSE_NOT_ACQUIRED_STATUSES.has(String(dto.status));

const DEFAULT_PAGE_SIZE = 10;

const pickStrongestLicense = (dto: IUserUsageUserConsumptionDto): string =>
    dto.strongestLicense
    ?? dto.licenses?.[0]
    ?? '';

const formatDateLabel = (epochSeconds?: number, locale?: string) => {
    if (!epochSeconds) {
        return '';
    }
    return new Date(epochSeconds * 1000).toLocaleDateString(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};

const mapRow = (dto: IUserUsageUserConsumptionDto): UserUsageRow => {
    const status = STATUS_MAP[String(dto.status)];
    const monthlyUsagePct = dto.entitlementPoolPercentConsumed == null
        ? null
        : Math.max(0, Math.min(100, Math.round(dto.entitlementPoolPercentConsumed)));
    const topUpPctLeft = dto.userPoolPercentConsumed != null
        ? Math.max(0, Math.min(100, Math.round(100 - dto.userPoolPercentConsumed)))
        : null;
    const paidUnits = status === 'consumingPaidUnits' ? 1 : null;
    return {
        id: dto.userId,
        name: dto.displayName ?? dto.userId,
        email: dto.email ?? '',
        license: pickStrongestLicense(dto),
        licenses: dto.licenses,
        monthlyUsagePct,
        topUpPacks: dto.topups ?? 0,
        topUpPctLeft,
        paidUnits,
        fallbackOn: true,
        status,
        needsAttention: status === 'consumingPaidUnits'
            ? { reason: 'consumingPaidUnits' }
            : undefined,
    };
};

const EMPTY_DISTRIBUTION = (unitCode: string): UserUsageDistribution => ({
    interval: 'annual',
    startLabel: '',
    endLabel: '',
    totalPltu: 0,
    remainingPltu: 0,
    reservedForTenants: 0,
    availableForTopUp: 0,
    unitCode,
});

export const loadUserUsageDistribution = async (unitCode: string, locale?: string): Promise<UserUsageDistribution> => {
    try {
        const dashboard = await getOrganizationConsumptionDashboard(unitCode);
        const remaining = Math.max(0, dashboard.totalCapacity - dashboard.totalConsumed);
        const reservedForTenants = Math.max(0, dashboard.totalAllocated - dashboard.consumedFromAllocated);
        const availableForTopUp = Math.max(0, remaining - reservedForTenants);
        return {
            interval: 'annual',
            startLabel: formatDateLabel(dashboard.startDate, locale),
            endLabel: formatDateLabel(dashboard.endDate, locale),
            totalPltu: dashboard.totalCapacity,
            remainingPltu: remaining,
            reservedForTenants,
            availableForTopUp,
            unitCode,
        };
    } catch {
        return EMPTY_DISTRIBUTION(unitCode);
    }
};

export interface UserUsageQueryFilters {
    licenseCodes?: string[];
    userId?: string;
}

export const loadUserUsageData = async (
    accountId: string,
    pageSize: number = DEFAULT_PAGE_SIZE,
    filters: UserUsageQueryFilters = {},
    skip: number = 0,
): Promise<UserUsageBundle> => {
    const safeCount = (p: Promise<IUserUsageOrgSummaryDto>) =>
        p.catch(() => ({ totalUsersWithConsumption: 0 }));
    const safeUsers = (p: ReturnType<typeof getUsersConsumption>) =>
        p.catch(() => ({
            totalCount: 0,
            results: [] as IUserUsageUserConsumptionDto[],
        }));
    const safeSingleUser = (p: ReturnType<typeof getSingleUserConsumption>) =>
        p.catch(() => undefined);

    const countPromise = safeCount(countUsersWithConsumption(accountId));

    if (filters.userId) {
        const [ summary, single ] = await Promise.all([
            countPromise,
            safeSingleUser(getSingleUserConsumption(accountId, filters.userId)),
        ]);
        const users = single && !isLicenseNotAcquired(single) ? [ mapRow(single) ] : [];
        return {
            users,
            needsAttention: users.filter((u) => u.needsAttention != null),
            periodOptions: [],
            totalUsersWithConsumption: summary.totalUsersWithConsumption,
            pageTotalCount: users.length,
        };
    }

    const licenseFilter = filters.licenseCodes && filters.licenseCodes.length > 0
        ? filters.licenseCodes
        : undefined;
    const [ summary, page ] = await Promise.all([
        countPromise,
        safeUsers(getUsersConsumption(accountId, {
            top: pageSize,
            skip,
        }, licenseFilter)),
    ]);

    const users = (page.results ?? [])
        .filter((dto) => !isLicenseNotAcquired(dto))
        .map(mapRow);
    const needsAttention = users.filter((u) => u.needsAttention != null);

    return {
        users,
        needsAttention,
        periodOptions: [],
        totalUsersWithConsumption: summary.totalUsersWithConsumption,
        pageTotalCount: page.totalCount,
    };
};

const EXPORT_PAGE_SIZE = 50;
// backstop against a backend that ignores `skip` and omits `totalCount` — degrade to a visible error instead of looping until the tab runs out of memory
const EXPORT_MAX_ROWS = 100_000;

export const loadAllUserUsageRows = async (
    accountId: string,
    filters: UserUsageQueryFilters = {},
): Promise<UserUsageRow[]> => {
    if (filters.userId) {
        try {
            const single = await getSingleUserConsumption(accountId, filters.userId);
            return single && !isLicenseNotAcquired(single) ? [ mapRow(single) ] : [];
        } catch (error) {
            // a missing user just means there is nothing to export; anything else must surface
            if ((error as { response?: { status?: number } })?.response?.status === 404) {
                return [];
            }
            throw error;
        }
    }

    const licenseFilter = filters.licenseCodes && filters.licenseCodes.length > 0
        ? filters.licenseCodes
        : undefined;

    const rows: UserUsageRow[] = [];
    let skip = 0;
    let totalCount = Infinity;
    let rawFetched = 0;
    while (rawFetched < totalCount) {
        if (rows.length >= EXPORT_MAX_ROWS) {
            throw new Error(`User usage export exceeded the ${EXPORT_MAX_ROWS} row limit`);
        }
        const page = await getUsersConsumption(accountId, {
            top: EXPORT_PAGE_SIZE,
            skip,
        }, licenseFilter);
        const results = page.results ?? [];
        // an empty page means the server has no more data, regardless of totalCount
        if (results.length === 0) {
            break;
        }
        rows.push(...results.filter((dto) => !isLicenseNotAcquired(dto)).map(mapRow));
        rawFetched += results.length;
        // keep paging on a missing totalCount until an empty page ends the loop
        totalCount = page.totalCount ?? totalCount;
        skip += results.length;
    }
    if (Number.isFinite(totalCount) && rawFetched < totalCount) {
        throw new Error(`User usage export incomplete: fetched ${rawFetched} of ${totalCount} users`);
    }
    return rows;
};

export const loadUserUsageCounts = async (
    accountId: string,
): Promise<{ total: number; attention: number }> => {
    const summary = await countUsersWithConsumption(accountId);
    return {
        total: summary.totalUsersWithConsumption,
        attention: summary.totalUsersWithConsumption,
    };
};
